<template>
  <VDataGridV2
    :data="documents"
    layout="fill"
    sticky
  >
    <VDataGridColumnV2 id="id"
                       label="№"
                       content-align="center"
                       width="120px">
      <template #default="{ row }">
        <VLink @click="openDocumentDialog(row)">{{ row.id }} </VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 
      id="buyersCode"
      label="Арт"
      content-align="center" 
      width="85px"
      content-class="form"
    >
      <template #default="{ row }">
        <!-- Product: артикул всегда инпутом, необязательный, пишется в code; у товара из справочника — только для чтения.
             Если по введённому артикулу нашёлся товар — подставляем его целиком -->
        <template v-if="row instanceof Product">
          <VInput v-model="row.code"
                  :max-length="60"
                  @input="setProductByCodeDebounced($event, row)" />
        </template>
        <template v-else>
          {{ row.buyersCode || row.code || '--' }}
        </template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="name"
      width="250px"
      content-class="ellipsis form"
    >
      <template #label>
        Товар
        <div class="description">Производитель</div>
      </template>
      <template #default="{ row }">
        <!-- Product: название всегда инпутом, обязательное; у товара из справочника — только для чтения -->
        <template v-if="row instanceof Product">
          <VInput v-model="row.name"
                  required
                  :max-length="175"
                  :disabled="row.isExisting === true"
          />
          <div class="description ellipsis" :title="row.manufacturer.name" v-if="row.manufacturer">
            {{ row.manufacturer.name }}
          </div>
        </template>
        <template v-else>
          <span class="ellipsis" :title="row.name">
            {{ row.name || '--' }}
          </span>
          <div class="description ellipsis" :title="row.manufacturer.name" v-if="row.manufacturer">
            {{ row.manufacturer.name }}
          </div>
        </template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 id="qty"
                       label="Кол-во"
                       content-align="right"
                       width="150px"
                       content-class="ellipsis form"
    >
      <template #default="{ row }">
        <!-- И для Product, и для OrderPosition qty редактируется на уровне позиции ЗТК,
             а не в документе: у позиции заказа своё количество (заказанное), его не трогаем.
             Единица измерения выводится в отдельной колонке.
             Без v-model: запись qty и пересчёт объявленной ценности идут в setQty
             в явном порядке -->
          <VInputNumberRusV2
            :model-value="getPosition(row).qty"
            :min="0"
            :type="Qty"
            required
            @update:model-value="setQty(row, $event)"
          />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="unit"
      label="Ед. изм."
      content-align="right"
      width="120px"
      content-class="form"
    >
      <template #default="{ row }">
        <!-- Единица измерения — свойство товара: у товара из справочника только для чтения.
             У остальных документов — просто текст -->
        <template v-if="row instanceof Product">
          <VSelectV2
            v-model="row.unit"
            required
            hide-clear-btn
            layout="vertical"
            :data="UnitsService.units"
            :disabled="row.isExisting === true"
          />
        </template>
        <template v-else>
          {{ row.unit?.name || '--' }}
        </template>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="!transportOrder.isInternal"
      id="priceFullSeller"
      label="Полная цена поставщика"
      content-align="right"
      width="200px"
      content-class="form"
    >
      <template #default="{ row }">
        {{ row.priceFullSeller?.format(row.currencyCode) || '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2 
      id="actions"
      width="52px" 
      content-class="form"
    >
      <template #default="{ row }">
        <VButton
          material="delete"
          @click="deleteDocument(row)"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2 class="centered">
      <template #default="{ row }">
        <VLink
          icon-postfix
          material="add"
          @click="openPicker"
        >
          <template v-if="transportOrder.isInternal === true">Добавить продукт</template>
          <template v-else>Позицию заказа</template>
        </VLink>

        <VLink
          v-if="this.transportOrder.isInternal"
          icon-postfix
          material="add"
          @click="addImplicitly"
        >
          Создать продукт
        </VLink>
      </template>

    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<style lang="scss" src="./ProductsGrid.scss"/>

<script lang="ts">
import TransportOrder from '@/domains/TransportOrders/TransportOrder';
import TransportOrderPosition from '@/domains/TransportOrders/TransportOrderPosition';
import OrderPosition from '@/domains/Orders/OrderPosition';
import Product from '@/domains/Products/Product';
import ProductsService from "@/domains/Products/ProductsService";
import DemandsService from '@/domains/Demand/DemandsService';
import Qty from '@/core/types/Qty';
import UnitsService from '@/domains/Units/UnitsService';
import debounce from "lodash.debounce";

export default defineComponent({
  name: 'ProductsGrid',

  props: {
    transportOrder: {
      type: TransportOrder,
      required: true,
    },
  },

  setup() {
    return {
      Product,
      Qty,
      UnitsService
    };
  },

  computed: {
    documents(): (OrderPosition | Product)[] {
      return this.transportOrder.positions
        .filter((p: TransportOrderPosition) => p.documentClass === 'OrderPosition' || p.documentClass === 'Product')
        .map((p: TransportOrderPosition) => p.document) as (OrderPosition | Product)[];
    },
  },

  methods: {
    deleteDocument(document: OrderPosition | Product): void {
      const position = this.getPosition(document);
      if (!position) return;

      this.transportOrder.deletePosition(position);
    },

    /**
     * Ищем по ссылке на документ, а не по id: у товаров, созданных вручную,
     * id нет, и по нему все они находили бы одну и ту же позицию.
     * documents отдаёт те же объекты, что лежат в позициях.
     */
    getPosition(document: OrderPosition | Product): TransportOrderPosition {
      return this.transportOrder.positions.find(
        (p: TransportOrderPosition) => p.document === document,
      );
    },

    /**
     * Объявленная ценность ЗТК хранится полем и считается от qty позиций,
     * поэтому сначала пишем количество, потом пересчитываем.
     * Порядок обработчиков v-model и @update:model-value на одном компоненте
     * не гарантируем — делаем оба шага здесь.
     */
    setQty(document: OrderPosition | Product, value: Qty): void {
      this.getPosition(document).qty = value;

      this.transportOrder.calculateDeclaredCost();
    },

    openPicker(): void {
      if (this.transportOrder.isInternal) {
        this.$core.dialogs.ProductsPicker.show({
          transportOrder: this.transportOrder,
        });
      } else {
        // Для internal перемещаем Product (ветка выше), для остальных — OrderPosition.
        // тип документа передаётся в пикер пропом, не через localStorage
        this.$core.dialogs.TransportableDocumentsPicker.show({
          transportOrder: this.transportOrder,
          documentClass:  ['OrderPosition'],
        });
      }
    },

    openDocumentDialog(row: any): void {
      if (!row.id) return;

      if (row instanceof Product) {
        ProductsService.editProduct(row.id);
      } else {
        // todo сервис этим должен заниматься
        this.$core.dialogs.OrderPositionDialog.show({entityId: row.id});
      }
    },

    setProductByCodeDebounced: debounce(async function(productCode: string, product: Product) {
      await this.setProductByCode(productCode, product);
    }, 400),

    /**
     * Ищет товар по артикулу и, если нашёл, подставляет его в позицию вместо введённого вручную.
     * Количество хранится в позиции ЗТК, поэтому при замене документа не теряется.
     */
    async setProductByCode(productCode: string, product: Product): Promise<void> {
      const products = await DemandsService.findProductsByCodes([productCode]);

      if (this.$core.utils.isNotEmptyArray(products) === true) {
        const foundProduct = await ProductsService.get(products[0].productid);

        if (this.$core.utils.isNullOrUndefined(foundProduct) === false) {
          this.getPosition(product).document = foundProduct;

          this.transportOrder.calculateDeclaredCost();
        }
        else {
          // товар по id не загрузился — оставляем введённый вручную
        }
      }
      else {
        // по артикулу ничего не нашли — оставляем введённый вручную
      }
    },

    /** Товар без справочника: пользователь вводит артикул (если необходимо), название и количество сам */
    addImplicitly() {
      const position = new TransportOrderPosition();
      position.documentClass = 'Product';
      position.document = Product.getEmpty();
      this.transportOrder.addPosition(position);
    }
  },
});
</script>